import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';

class NetatmoFirebaseService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 300000; // 5 minutes
    this.requestQueue = [];
    this.isProcessingQueue = false;
    this.lastRequestTime = 0;
    this.minDelay = 1000; // Délai minimum entre les requêtes (1 seconde)
  }

  /**
   * Ajouter une requête à la queue avec rate limiting
   */
  async addToQueue(requestFn) {
    return new Promise((resolve, reject) => {
      this.requestQueue.push({ requestFn, resolve, reject });
      this.processQueue();
    });
  }

  /**
   * Traiter la queue de requêtes avec délai
   */
  async processQueue() {
    if (this.isProcessingQueue || this.requestQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;

    while (this.requestQueue.length > 0) {
      const { requestFn, resolve, reject } = this.requestQueue.shift();
      
      // Attendre le délai minimum entre les requêtes
      const now = Date.now();
      const timeSinceLastRequest = now - this.lastRequestTime;
      if (timeSinceLastRequest < this.minDelay) {
        await new Promise(res => setTimeout(res, this.minDelay - timeSinceLastRequest));
      }
      
      try {
        const result = await requestFn();
        this.lastRequestTime = Date.now();
        resolve(result);
      } catch (error) {
        reject(error);
      }
    }

    this.isProcessingQueue = false;
  }

  /**
   * Récupère les données de pluie pour un compte avec rate limiting
   */
  async getRainData(accountId, period, options = {}) {
    // Créer une clé de cache plus explicite
    const keyParts = [
      'rain',
      accountId,
      period,
      options.selectedYear || 'current',
      options.selectedMonth || 'all',
      options.startDate || '',
      options.endDate || ''
    ];
    const cacheKey = keyParts.join('_');
    
    // Vérifier le cache en priorité
    if (!options.forceRefresh) {
      const cached = this.cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
        return cached.data;
      }
    }
    
    // Ajouter à la queue pour éviter les appels simultanés
    return this.addToQueue(async () => {
      try {
        const getRainData = httpsCallable(functions, 'netatmoGetRainData');
        const result = await getRainData({
          accountId,
          period,
          ...options
        });
        
        // Mettre en cache
        this.cache.set(cacheKey, {
          data: result.data,
          timestamp: Date.now()
        });
        
        return result.data;
      } catch (error) {
        console.error('Erreur getRainData:', error);
        throw error;
      }
    });
  }

  /**
   * Récupère les années disponibles dans le cache
   */
  async getAvailableYears(accountId) {
    try {
      const getAvailableYears = httpsCallable(functions, 'netatmoGetAvailableYears');
      const result = await getAvailableYears({ accountId });
      return result.data;
    } catch (error) {
      console.error('Erreur getAvailableYears:', error);
      return [];
    }
  }

  /**
   * Récupère les données agrégées de tous les comptes avec rate limiting
   */
  async getAggregatedData(period, options = {}) {
    const cacheKey = `aggregated_${period}_${JSON.stringify(options)}`;
    
    // Vérifier le cache en priorité
    if (!options.forceRefresh) {
      const cached = this.cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
        return cached.data;
      }
    }
    
    // Ajouter à la queue pour éviter les appels simultanés
    return this.addToQueue(async () => {
      try {
        const getAggregatedData = httpsCallable(functions, 'netatmoGetAggregatedData');
        const result = await getAggregatedData({
          period,
          ...options
        });
        
        // Mettre en cache
        this.cache.set(cacheKey, {
          data: result.data,
          timestamp: Date.now()
        });
        
        return result.data;
      } catch (error) {
        console.error('Erreur getAggregatedData:', error);
        throw error;
      }
    });
  }

  /**
   * Liste tous les comptes disponibles
   */
  async listAccounts() {
    try {
      const listAccounts = httpsCallable(functions, 'netatmoListAccounts');
      const result = await listAccounts();
      return result.data.accounts;
    } catch (error) {
      console.error('Erreur listAccounts:', error);
      throw error;
    }
  }

  /**
   * Vide le cache local
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Formate les données pour l'affichage selon la période
   */
  formatDataForPeriod(data, period) {
    
    // Vérifier la structure des données - elles peuvent être dans data.data directement
    let dataArray;
    if (!data) {
      return { chartData: [], tableData: [], summary: {} };
    }
    
    if (data.data && Array.isArray(data.data.data)) {
      // Structure: data.data.data (ancien format)
      dataArray = data.data.data;
    } else if (data.data && Array.isArray(data.data)) {
      // Structure: data.data (nouveau format)
      dataArray = data.data;
    } else if (Array.isArray(data)) {
      // Structure: data directement
      dataArray = data;
    } else {
      return { chartData: [], tableData: [], summary: {} };
    }

    let chartData = [];
    let tableData = [];
    
    
    switch (period) {
      case 'today':
      case '24h':
        // Données par heure
        chartData = dataArray.map(item => ({
          name: new Date(item.timestamp).toLocaleTimeString('fr-FR', { 
            hour: '2-digit', 
            minute: '2-digit' 
          }),
          value: item.value || 0
        }));
        
        tableData = dataArray.map(item => ({
          période: new Date(item.timestamp).toLocaleTimeString('fr-FR'),
          fullDate: new Date(item.timestamp).toLocaleString('fr-FR'),
          total: item.value || 0
        }));
        break;
        
      case 'week':
        // Données par jour (jours avec pluie uniquement)
        const weeklyData = {};
        
        dataArray.forEach(item => {
          const date = new Date(item.timestamp).toLocaleDateString('fr-FR');
          if (!weeklyData[date]) {
            weeklyData[date] = 0;
          }
          weeklyData[date] += item.value || 0;
        });
        
        
        chartData = Object.entries(weeklyData)
          .filter(([_, value]) => value > 0)
          .map(([date, value]) => ({
            name: date,
            value: Math.round(value * 1000) / 1000 // Plus de précision
          }));
        
        
        tableData = chartData.map(item => ({
          période: item.name,
          fullDate: item.name,
          total: item.value
        }));
        break;
        
      case 'month':
        // Données par jour sur 30 jours
        const monthlyData = {};
        dataArray.forEach(item => {
          const date = new Date(item.timestamp).toLocaleDateString('fr-FR');
          if (!monthlyData[date]) {
            monthlyData[date] = 0;
          }
          monthlyData[date] += item.value || 0;
        });
        
        chartData = Object.entries(monthlyData).map(([date, value]) => ({
          name: date,
          value: Math.round(value * 10) / 10
        }));
        
        tableData = chartData.map(item => ({
          période: item.name,
          fullDate: item.name,
          total: item.value
        }));
        break;
        
      case 'year':
        // Les données sont déjà agrégées par mois côté backend
        
        chartData = dataArray.map(item => {
          
          return {
            name: item.monthName || new Date(item.timestamp).toLocaleDateString('fr-FR', { 
              month: 'long', 
              year: 'numeric' 
            }),
            value: Math.round((item.value || 0) * 10) / 10,
            month: item.month,
            year: item.year
          };
        });
        
        tableData = chartData.map(item => ({
          période: item.name,
          fullDate: item.name,
          total: item.value
        }));
        break;
        
      default:
        chartData = dataArray.map(item => ({
          name: new Date(item.timestamp).toLocaleDateString('fr-FR'),
          value: item.value || 0
        }));
        
        tableData = chartData.map(item => ({
          période: item.name,
          fullDate: item.name,
          total: item.value
        }));
    }
    
    const result = {
      chartData,
      tableData,
      summary: data.summary || {}
    };
    
    
    return result;
  }
}

export default new NetatmoFirebaseService();